import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, auth, storage } from "../../firebase";
import {
  doc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  deleteDoc,
  addDoc,
  onSnapshot,
  updateDoc,
  getDoc,
} from "firebase/firestore";

/* -------------------------------------------------------------------------- */
/* 🔹 Helper: Upload Verification Documents                                   */
/* -------------------------------------------------------------------------- */
const uploadVerificationDocuments = async (documents, ownerId, locationId) => {
  if (!documents || documents.length === 0) return [];

  const uploadedDocs = [];

  for (const docItem of documents) {
    if (!docItem.file) continue;

    const path = `vendorUsers/${ownerId}/branchStores/${locationId}/verification/${
      docItem.id || docItem.type
    }_${Date.now()}_${docItem.file.name}`;
    const storageRef = ref(storage, path);

    await uploadBytes(storageRef, docItem.file);
    const url = await getDownloadURL(storageRef);

    uploadedDocs.push({
      id: docItem.id, // e.g., 'ein', 'license'
      type: docItem.type, // e.g., 'EIN Certificate'
      name: docItem.file.name,
      url,
      verified: false,
    });
  }

  return uploadedDocs;
};

/* -------------------------------------------------------------------------- */
/* 🔹 Helper: Upload Store / Additional Images                                */
/* -------------------------------------------------------------------------- */
const uploadStoreImages = async (images, ownerId, locationId) => {
  if (!images || images.length === 0) return [];

  const uploaded = [];

  for (const img of images) {
    if (!img.file) continue;

    const path = `vendorUsers/${ownerId}/branchStores/${locationId}/storeImages/${Date.now()}_${
      img.file.name
    }`;
    const storageRef = ref(storage, path);

    await uploadBytes(storageRef, img.file);
    const url = await getDownloadURL(storageRef);

    uploaded.push({
      name: img.file.name,
      url,
    });
  }

  return uploaded;
};

/* -------------------------------------------------------------------------- */
/* 🔹 Helper: Prepare Firestore Payload                                       */
/* -------------------------------------------------------------------------- */
const prepareLocationPayload = (data, uploadedDocs, uploadedStoreImages) => {
  // Use safety checks (|| "") before trimming to prevent crashes
  return {
    ownerId: data.ownerId,
    ownerEmail: data.ownerEmail,
    ownerName: data.ownerName,
    
    // Step 1: Operational Info
    name: (data.name || "").trim(),
    address: (data.address || "").trim(),
    city: (data.city || "").trim(),
    state: (data.state || "").trim(),
    zip: (data.zip || "").trim(),
    contact: (data.contact || "").trim(),
    locationId: (data.locationId || "").trim(),
    
    // New Fields
    openTime: (data.openTime || "").trim(),
    closeTime: (data.closeTime || "").trim(),

    // Step 2: Legal Info
    businessName: (data.businessName || "").trim(),
    businessIdNumber: (data.businessIdNumber || "").trim(), // Tax ID / EIN
    
    // Arrays
    verificationDocuments: uploadedDocs || [],
    storeImages: uploadedStoreImages || [],
    
    // Metadata
    status: data.status || "pending",
    verifiedBy: data.verifiedBy !== undefined ? data.verifiedBy : null,
    verifiedAt: data.verifiedAt !== undefined ? data.verifiedAt : null,
    adminRemarks: data.adminRemarks !== undefined ? data.adminRemarks : "",
    createdAt: data.createdAt || serverTimestamp(),
  };
};

/* -------------------------------------------------------------------------- */
/* 🔹 CREATE: Add a New Location (inside vendorUsers/{ownerId}/locations)      */
/* -------------------------------------------------------------------------- */
const addLocation = async (data) => {
  try {
    const { ownerId, locationId, verificationDocuments, storeImages } = data;

    // Use a fallback locationId if one wasn't provided for the path
    const safeLocationId = locationId || Date.now().toString();

    // Upload all files
    const uploadedDocs = await uploadVerificationDocuments(
      verificationDocuments,
      ownerId,
      safeLocationId
    );
    const uploadedStoreImages = await uploadStoreImages(
      storeImages,
      ownerId,
      safeLocationId
    );

    // 1️⃣ Prepare data
    const payload = prepareLocationPayload(
      data,
      uploadedDocs,
      uploadedStoreImages
    );

    // 2️⃣ Save to Vendor's private branchStores (for their dashboard)
    const branchesRef = collection(db, "vendorUsers", ownerId, "branchStores");
    const branchDocRef = await addDoc(branchesRef, payload);

    // 3️⃣ Save to top-level storeVerificationRequests (for Admin approval)
    const verificationRequestRef = doc(db, "storeVerificationRequests", branchDocRef.id);
    await setDoc(verificationRequestRef, {
      ...payload,
      id: branchDocRef.id, // Keep the same ID for easy lookups
      ownerId,
      submittedAt: serverTimestamp(),
    });

    console.log(
      "✅ Location added and verification request sent. ID:",
      branchDocRef.id
    );
    return branchDocRef;
  } catch (error) {
    console.error("❌ addLocation Error:", error);
    throw error;
  }
};

/* -------------------------------------------------------------------------- */
/* 🔹 UPDATE: Update an Existing Location                                     */
/* -------------------------------------------------------------------------- */
const updateLocation = async (locationId, data) => {
  try {
    const { ownerId, verificationDocuments, storeImages } = data;

    // 1. Handle Verification Documents
    const finalVerificationDocs = [];
    for (const docItem of (verificationDocuments || [])) {
      if (docItem.file) {
        // New file upload
        const path = `vendorUsers/${ownerId}/branchStores/${locationId}/verification/${docItem.id || docItem.type}_${Date.now()}_${docItem.file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, docItem.file);
        const url = await getDownloadURL(storageRef);
        finalVerificationDocs.push({
          id: docItem.id,
          type: docItem.type,
          name: docItem.file.name,
          url,
          verified: false,
        });
      } else if (docItem.url || docItem.previewUrl) {
        // Keep existing
        finalVerificationDocs.push({
          id: docItem.id,
          type: docItem.type,
          name: docItem.name,
          url: docItem.url || docItem.previewUrl,
          verified: docItem.verified || false,
        });
      }
    }

    // 2. Handle Store Images
    const finalStoreImages = [];
    for (const img of (storeImages || [])) {
      if (img.file) {
        // New file upload
        const path = `vendorUsers/${ownerId}/branchStores/${locationId}/storeImages/${Date.now()}_${img.file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, img.file);
        const url = await getDownloadURL(storageRef);
        finalStoreImages.push({
          name: img.file.name,
          url,
        });
      } else if (img.url || img.previewUrl) {
        // Keep existing
        finalStoreImages.push({
          name: img.name,
          url: img.url || img.previewUrl,
        });
      }
    }

    // Prepare updated payload
    const payload = prepareLocationPayload(data, finalVerificationDocs, finalStoreImages);
    
    // If updating a store that isn't approved, reset it to pending for verification
    if (payload.status !== "approved") {
      payload.status = "pending";
    }

    // Safety check: prepareLocationPayload keeps data.status, so we're good.
    delete payload.createdAt;
    payload.updatedAt = serverTimestamp();

    const locationRef = doc(db, "vendorUsers", ownerId, "branchStores", locationId);
    await updateDoc(locationRef, payload);

    // If it's not approved yet, we need to create/update its entry in the verification queue
    if (payload.status === "pending") {
      const verificationRequestRef = doc(db, "storeVerificationRequests", locationId);
      await setDoc(verificationRequestRef, {
        ...payload,
        id: locationId,
        ownerId,
        updatedAt: serverTimestamp(),
      });
      console.log("✅ Re-submitted store for verification:", locationId);
    }

    console.log("✅ Location updated:", locationId);
    return true;
  } catch (error) {
    console.error("❌ updateLocation Error:", error);
    throw error;
  }
};

/* -------------------------------------------------------------------------- */

/* 🔹 READ: Get All Locations for Current User                                */
/* -------------------------------------------------------------------------- */
const getLocations = async () => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      console.warn("UserId is undefined skipping location listener");
      return [];
    }

    // Auto-migrate legacy locations to branchStores
    try {
      const oldLocationRef = collection(db, "vendorUsers", userId, "locations");
      const oldSnapshot = await getDocs(oldLocationRef);
      if (!oldSnapshot.empty) {
        console.log(`Found ${oldSnapshot.size} legacy locations. Migrating to branchStores...`);
        for (const locDoc of oldSnapshot.docs) {
          const newDocRef = doc(db, "vendorUsers", userId, "branchStores", locDoc.id);
          await setDoc(newDocRef, locDoc.data(), { merge: true });
          await deleteDoc(locDoc.ref); // Clean up old location
        }
        console.log("Migration complete.");
      }
    } catch (migErr) {
      console.error("Auto-migration failed (non-fatal):", migErr);
    }

    const locationCollectionRef = collection(
      db,
      "vendorUsers",
      userId,
      "branchStores"
    );
    const snapshot = await getDocs(locationCollectionRef);
    const locations = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return locations;
  } catch (error) {
    console.error("Error fetching locations:", error);
    throw error;
  }
};

/* -------------------------------------------------------------------------- */
/* 🔹 DELETE: Remove a Location                                               */
/* -------------------------------------------------------------------------- */
const deleteLocation = async (locationId) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      console.warn("UserId is undefined, skipping location delete");
      return false;
    }
    const locationDoc = doc(db, "vendorUsers", userId, "branchStores", locationId);
    await deleteDoc(locationDoc);

    try {
      // Note: mapLocations deletion is not performed here.
      // We rely on the core DB architecture for global map cleanup.
      console.log("✅ Deleted branch document:", locationId);
    } catch (mapErr) {
      console.error("❌ Failed to delete from mapLocations:", mapErr);
    }

    return true;
  } catch (error) {
    console.error("Error deleting location:", error);
    return false;
  }
};

/* -------------------------------------------------------------------------- */
/* 🔹 LISTEN: Real-time Location Count                                        */
/* -------------------------------------------------------------------------- */
const listenToLocationsCount = (setLocationsCount) => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    console.warn("userId is undefined, skipping count listener");
    return;
  }
  const collectionRef = collection(db, "vendorUsers", userId, "branchStores");

  const unsubscribe = onSnapshot(
    collectionRef,
    (snapshot) => {
      const count = snapshot.size;
      setLocationsCount(count); 
    },
    (error) => {
      console.error("Error listening to location count:", error);
    }
  );

  return unsubscribe;
};

/* -------------------------------------------------------------------------- */
/* 🔹 UPDATE: Update Images for Existing Location                             */
/* -------------------------------------------------------------------------- */
const updateLocationImages = async (locationId, filesToAdd, urlsToDelete) => {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("User not authenticated.");

  const locationRef = doc(db, "vendorUsers", userId, "branchStores", locationId);
  const locationSnap = await getDoc(locationRef);
  if (!locationSnap.exists()) throw new Error("Location not found.");

  // 1. Upload new files
  const uploadPromises = filesToAdd.map(async (file) => {
    const path = `vendorUsers/${userId}/branchStores/${locationId}/storeImages/${Date.now()}_${
      file.name
    }`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return { name: file.name, url };
  });
  const newImageObjects = await Promise.all(uploadPromises);

  // 2. Delete old files
  const deletePromises = urlsToDelete.map(async (url) => {
    try {
      const fileRef = ref(storage, url);
      await deleteObject(fileRef);
    } catch (error) {
      console.warn("Could not delete file:", url, error.code);
    }
  });
  await Promise.all(deletePromises);

  // 3. Update Firestore
  const currentImages = locationSnap.data().storeImages || [];
  const updatedImages = currentImages
    .filter((img) => !urlsToDelete.includes(img.url))
    .concat(newImageObjects);

  await updateDoc(locationRef, {
    storeImages: updatedImages,
    updatedAt: serverTimestamp(),
  });

  const finalDocSnap = await getDoc(locationRef);
  return { id: finalDocSnap.id, ...finalDocSnap.data() };
};

export {
  addLocation,
  getLocations,
  deleteLocation,
  updateLocation,
  listenToLocationsCount,
  updateLocationImages,
};